/**
 * The `/feed` screen's persisted state (§12.4, FEED_SPEC) and its translation into #51 params.
 *
 * `{range, filters, sort}` is stored server-side at `feed:main`. The filter vocabulary is the view
 * screen's minus `q`/`authorIds` plus the scope (`groupIds`/`profileIds`), so everything shared is
 * sanitized and serialized by `viewState` — this module owns only what is new: the scope, the
 * sidebar's section arithmetic, and the pruning of ids that no longer exist.
 */

import type { FeedFiltersState, FeedUiState, GlobalFeedQuery, ViewRangeState } from '@lw/shared';
import {
  DEFAULT_VIEW_STATE,
  pickIdList,
  sanitizeRange,
  sanitizeSort,
  sanitizeViewFilters,
  toFilterParams,
  type ResolvedRange,
  type ViewSort,
} from '../view/viewState';

export type FeedState = {
  range: ViewRangeState;
  filters: FeedFiltersState;
  sort: ViewSort;
};

export const DEFAULT_FEED_STATE: FeedState = {
  range: { ...DEFAULT_VIEW_STATE.range },
  filters: {},
  sort: { ...DEFAULT_VIEW_STATE.sort },
};

/* --------------------------------------------------------------- sections */

/** Sidebar sections, top to bottom (FEED_SPEC § Filter sidebar). */
export const FEED_SECTIONS = [
  'groups',
  'profiles',
  'postTypes',
  'contentTypes',
  'performance',
  'attributes',
  'length',
] as const;
export type FeedSectionId = (typeof FEED_SECTIONS)[number];

export const FEED_SECTION_LABEL: Record<FeedSectionId, string> = {
  groups: 'Groups',
  profiles: 'Profiles',
  postTypes: 'Post type',
  contentTypes: 'Content type',
  performance: 'Performance',
  attributes: 'Attributes',
  length: 'Length',
};

/**
 * Open on a fresh session: none. Every section starts collapsed, so the island opens as a short
 * list of headings the whole filter vocabulary is visible in at once, rather than two long member
 * lists pushing the other five sections below the fold.
 */
export const DEFAULT_OPEN_SECTIONS: FeedSectionId[] = [];

/**
 * What the section's chip shows. Checked items for the lists, one per performance condition, one
 * per non-"Any" tri-state for Attributes, 1 for Length once a bound is set (a remembered unit on
 * its own is not a filter) — so the footer's total is the number of *choices* made.
 */
export function sectionCount(filters: FeedFiltersState, section: FeedSectionId): number {
  switch (section) {
    case 'groups':
      return filters.groupIds?.length ?? 0;
    case 'profiles':
      return filters.profileIds?.length ?? 0;
    case 'postTypes':
      return filters.postTypes?.length ?? 0;
    case 'contentTypes':
      return filters.contentTypes?.length ?? 0;
    case 'performance':
      return filters.perf?.length ?? 0;
    case 'attributes':
      return (
        (filters.hasMedia === undefined ? 0 : 1) +
        (filters.edited === undefined ? 0 : 1) +
        (filters.hasLink === undefined ? 0 : 1)
      );
    case 'length':
      return filters.lenUnit !== undefined &&
        (filters.lenMin !== undefined || filters.lenMax !== undefined)
        ? 1
        : 0;
  }
}

/** Sum of the section chips — the footer's "Clear N filters". Range and sort are header state. */
export function totalFilterCount(filters: FeedFiltersState): number {
  return FEED_SECTIONS.reduce((total, section) => total + sectionCount(filters, section), 0);
}

/** The ⓧ on a section chip: that section only, never the others. */
export function clearSection(filters: FeedFiltersState, section: FeedSectionId): FeedFiltersState {
  const next: FeedFiltersState = { ...filters };
  switch (section) {
    case 'groups':
      delete next.groupIds;
      break;
    case 'profiles':
      delete next.profileIds;
      break;
    case 'postTypes':
      delete next.postTypes;
      break;
    case 'contentTypes':
      delete next.contentTypes;
      break;
    case 'performance':
      delete next.perf;
      break;
    case 'attributes':
      delete next.hasMedia;
      delete next.edited;
      delete next.hasLink;
      break;
    case 'length':
      delete next.lenUnit;
      delete next.lenMin;
      delete next.lenMax;
      break;
  }
  return next;
}

/**
 * Drops selected ids that no longer exist, so the chips stay honest after a group is deleted or a
 * profile archived. Returns `null` when nothing changed — the caller must not write on every load.
 * The server ignores dangling ids regardless; this is about what the sidebar claims.
 */
export function pruneScope(
  filters: FeedFiltersState,
  liveGroupIds: ReadonlySet<number>,
  liveProfileIds: ReadonlySet<number>,
): FeedFiltersState | null {
  const groupIds = filters.groupIds?.filter((id) => liveGroupIds.has(id));
  const profileIds = filters.profileIds?.filter((id) => liveProfileIds.has(id));
  const groupsChanged = groupIds !== undefined && groupIds.length !== filters.groupIds?.length;
  const profilesChanged =
    profileIds !== undefined && profileIds.length !== filters.profileIds?.length;
  if (!groupsChanged && !profilesChanged) return null;

  const next: FeedFiltersState = { ...filters };
  if (groupIds === undefined || groupIds.length === 0) delete next.groupIds;
  else next.groupIds = groupIds;
  if (profileIds === undefined || profileIds.length === 0) delete next.profileIds;
  else next.profileIds = profileIds;
  return next;
}

/* --------------------------------------------------------------- sanitize */

/** Stored UI state is user-writable JSON: never trust its shape, always fall back to defaults. */
export function sanitizeFeedState(raw: unknown): FeedState {
  if (typeof raw !== 'object' || raw === null) {
    return { ...DEFAULT_FEED_STATE, filters: {}, range: { ...DEFAULT_FEED_STATE.range } };
  }
  const source = raw as Record<string, unknown>;
  const shared = sanitizeViewFilters(source.filters);
  // The params the global feed has no place for: search is Posts-only, the scope params replace
  // `authorIds`, and the Saved tri-state has no control here — an invisible filter is worse than a
  // missing one. A blob copied from a view key therefore loses exactly those three.
  delete shared.q;
  delete shared.authorIds;
  delete shared.saved;

  const filters: FeedFiltersState = shared;
  const rawFilters =
    typeof source.filters === 'object' && source.filters !== null
      ? (source.filters as Record<string, unknown>)
      : {};
  const groupIds = pickIdList(rawFilters.groupIds);
  if (groupIds) filters.groupIds = groupIds;
  const profileIds = pickIdList(rawFilters.profileIds);
  if (profileIds) filters.profileIds = profileIds;

  return {
    range: sanitizeRange(source.range),
    filters,
    sort: sanitizeSort(source.sort),
  };
}

/** What actually goes to `PUT /api/ui-state/feed:main` (the shape of `FeedUiStateSchema`). */
export function toStoredFeedState(state: FeedState): FeedUiState {
  return { range: state.range, filters: state.filters, sort: state.sort };
}

/* ----------------------------------------------------------------- params */

/**
 * #51 request params. The §5.2 half goes through `toFilterParams` — the same function the view
 * screens use — so a filter cannot mean one thing here and another there; only the scope is added.
 * `isGroup: false` keeps `authorIds` out even if one somehow survived into the stored blob.
 */
export function toFeedParams(state: FeedState, resolved: ResolvedRange): Partial<GlobalFeedQuery> {
  const params: Partial<GlobalFeedQuery> = {
    ...toFilterParams(state.filters, resolved, { isGroup: false }),
    sort: state.sort.key,
    dir: state.sort.dir,
  };
  if (state.filters.groupIds?.length) params.groupIds = state.filters.groupIds;
  if (state.filters.profileIds?.length) params.profileIds = state.filters.profileIds;
  return params;
}
